import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import Button from "@mui/material/Button";
import DescriptionIcon from "@mui/icons-material/Description";
import { InfoNote } from "../components/preliminary-notification/primitives";
import { ValidationContext, validateSubmission } from "../components/preliminary-notification/validation";
import StepIndicator from "../components/preliminary-notification/StepIndicator";
import FormNavButtons from "../components/preliminary-notification/FormNavButtons";
import PreFormPopup from "../components/preliminary-notification/popup/PreFormPopup";
import ConfirmSubmitPopup from "../components/preliminary-notification/popup/ConfirmSubmitPopup";
import AgencyInfoStep from "../components/preliminary-notification/steps/AgencyInfoStep";
import DirectChargesStep from "../components/preliminary-notification/steps/DirectChargesStep";
import AuthorizedSignerStep from "../components/preliminary-notification/steps/AuthorizedSignerStep";
import CertificationStep from "../components/preliminary-notification/steps/CertificationStep";
import {
  createEmptyEntry,
  fetchJurisdictions,
  fetchJurisdictionContacts,
  fetchAuthorizedSigners,
  fetchLatestPreliminaryNotification,
  fetchLatestPreliminaryNotificationBySubagency,
  submitPreliminaryNotification,
  resendConfirmationEmail,
  buildSubmissionPayload,
  parsePhoneAndExtension,
  selectionKey,
  apiErrorMessage
} from "../components/preliminary-notification/data";
import {
  buildSubmissionPdfBase64,
  openSubmissionPdfInNewTab
} from "../components/preliminary-notification/pdf";
import { logError } from "../utils/logger";
import { colors } from "../utils/styles";
import { fmtDate } from "../utils/dateFormat";

// Stable identity for the pre-Submit provider value — prevents every primitive
// from re-rendering on every keystroke just because we passed a fresh `[]`.
const NO_VALIDATION_ERRORS = [];

export default function PreliminaryNotification() {
  const [step, setStep] = useState(0);
  // selection is { kind: "group", groupId } | { kind: "parent", parentCode } | null
  const [selection, setSelection] = useState(null);
  const [nextId, setNextId] = useState(2);
  const [entries, setEntries] = useState([createEmptyEntry(1)]);
  const [expanded, setExpanded] = useState(new Set([1]));
  const [signers, setSigners] = useState([]);
  const [prep, setPrep] = useState({ first: "", last: "", phone: "", phoneExtension: "", email: "" });
  const [confirm, setConfirm] = useState({ preparer: true, contacts: false, signer: false });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [parentOptions, setParentOptions] = useState([]);
  // Distinguishes "fetch in flight" from "fetch done, zero agencies" so the
  // Parent Agency dropdown doesn't show "Loading..." forever on an empty result.
  const [jurisdictionsLoaded, setJurisdictionsLoaded] = useState(false);
  const [subagenciesBySelection, setSubagenciesBySelection] = useState({});
  // Caches that the signer / latest-submission effects read on selection change.
  // Refs (not state) so writing a new entry doesn't retrigger the effect — that
  // would clobber any user edits to phone/email between selection changes.
  const signersBySelectionRef = useRef({});
  const latestBySelectionRef = useRef({});
  const [latestSubmission, setLatestSubmission] = useState(null);
  const [contactsByCode, setContactsByCode] = useState({});
  const [latestByCode, setLatestByCode] = useState({});
  // Subagency codes whose prior-submission prefill has already been decided
  // (applied or skipped due to user edits). Lives at the page so it survives
  // SubagencyCard unmount on step navigation — otherwise the prefill effect
  // would re-run on remount and clobber edits.
  const prefilledCodesRef = useRef(new Set());
  const [loadError, setLoadError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitResult, setSubmitResult] = useState(null);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);
  const [resendError, setResendError] = useState(null);
  // Set true *only* on a Submit click that finds errors. The effect below
  // consumes the flag, so subsequent edits or re-Submits don't re-scroll.
  const shouldScrollOnNextRenderRef = useRef(false);

  const token = useMemo(() => {
    if (typeof window === "undefined") return "";
    return new URLSearchParams(window.location.search).get("token") || "";
  }, []);
  const [userEmail, setUserEmail] = useState("");
  const [taxRollYear, setTaxRollYear] = useState("");
  const [submissionPeriodError, setSubmissionPeriodError] = useState(null);
  const otpDisabled = process.env.REACT_APP_DISABLE_OTP === "true";
  const [verified, setVerified] = useState(false);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const apiRoot = process.env.REACT_APP_API_ROOT || "";
    
    // Fetch both decrypted email and submission period status
    Promise.all([
      fetch(`${apiRoot}/Encryption/decrypt?token=${encodeURIComponent(token)}`)
        .then((res) => {
          if (!res.ok) throw new Error(`decrypt ${res.status}`);
          return res.text();
        }),
      fetch(`${apiRoot}/submission-period/status`)
        .then((res) => {
          if (!res.ok) throw new Error(`status ${res.status}`);
          return res.json();
        })
    ])
      .then(([email, status]) => {
        if (cancelled) return;
        setUserEmail(email);
        
        if (!status.isOpen) {
          setSubmissionPeriodError(status.message || 'Submission period is closed.');
        } else {
          setTaxRollYear(status.taxRollYear || '');
        }
      })
      .catch((err) => {
        if (!cancelled) {
          logError("Failed to initialize form:", err);
          setLoadError(err.message);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    let cancelled = false;
    fetchJurisdictions(token)
      .then((data) => {
        if (cancelled) return;
        setParentOptions(data.options || []);
        setSubagenciesBySelection(data.subagenciesBySelection || {});
      })
      .catch((err) => {
        if (cancelled) return;
        logError("Failed to load jurisdictions:", err);
        setLoadError(err.message);
      })
      .finally(() => {
        if (!cancelled) setJurisdictionsLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const selectionCacheKey = selectionKey(selection);

  // Fetch authorized signers whenever the selection changes. Caches the server
  // response per selection and replaces the editable `signers` state with a
  // fresh copy when switching selections.
  useEffect(() => {
    if (!selection) return;
    const cached = signersBySelectionRef.current[selectionCacheKey];
    if (cached) {
      setSigners(cached.map((s) => ({ ...s })));
      return;
    }
    let cancelled = false;
    fetchAuthorizedSigners(selection)
      .then((arr) => {
        if (cancelled) return;
        signersBySelectionRef.current[selectionCacheKey] = arr;
        setSigners(arr.map((s) => ({ ...s })));
      })
      .catch((err) => logError("Failed to load authorized signers:", err));
    return () => {
      cancelled = true;
    };
  }, [selection, selectionCacheKey]);

  // Fetch the latest prior submission when the user reaches the Authorized Signer step.
  useEffect(() => {
    if (step !== 2 || !selection) return;
    const cached = latestBySelectionRef.current[selectionCacheKey];
    if (cached !== undefined) {
      setLatestSubmission(cached);
      return;
    }
    let cancelled = false;
    fetchLatestPreliminaryNotification(selection)
      .then((latest) => {
        if (cancelled) return;
        latestBySelectionRef.current[selectionCacheKey] = latest;
        setLatestSubmission(latest);
        if (latest && Array.isArray(latest.signers) && latest.signers.length > 0) {
          // Per-index prefill: copy a prior submission's phone/email into any
          // signer slots the user hasn't already filled.
          setSigners((current) =>
            current.map((s, i) => {
              const prior = latest.signers[i];
              if (!prior) return s;
              const priorParts = parsePhoneAndExtension(prior.phone);
              return {
                ...s,
                phone: s.phone || priorParts.phone || "",
                phoneExtension: s.phoneExtension || priorParts.phoneExtension || "",
                email: s.email || prior.email || ""
              };
            })
          );
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [step, selection, selectionCacheKey]);

  // Per-subagency loaders. Cached at the parent level so the data survives
  // step navigation. Reset when selection changes (see handleSelectionChange).
  const loadContacts = useCallback((code) => {
    if (!code) return;
    setContactsByCode((prev) => {
      if (code in prev) return prev;
      fetchJurisdictionContacts(code)
        .then((list) => setContactsByCode((p) => ({ ...p, [code]: list })))
        .catch((err) => {
          logError(`Failed to load contacts for ${code}:`, err);
          setContactsByCode((p) => ({ ...p, [code]: [] }));
        });
      return { ...prev, [code]: null };
    });
  }, []);

  const loadLatest = useCallback(
    (code) => {
      if (!selection || !code) return;
      setLatestByCode((prev) => {
        if (code in prev) return prev;
        fetchLatestPreliminaryNotificationBySubagency(selection, code)
          .then((latest) => setLatestByCode((p) => ({ ...p, [code]: latest })))
          .catch(() => setLatestByCode((p) => ({ ...p, [code]: null })));
        return { ...prev, [code]: null };
      });
    },
    [selection]
  );

  const subagencyData = useMemo(
    () => ({ contactsByCode, latestByCode, loadContacts, loadLatest, prefilledCodesRef }),
    [contactsByCode, latestByCode, loadContacts, loadLatest, prefilledCodesRef]
  );

  // Submission date + ID come from the server response, so they're only
  // populated after a successful POST. Before submit they stay empty.
  const submissionDate = useMemo(() => fmtDate(submitResult?.submittedAt), [submitResult]);
  const submissionId = submitResult?.submissionIdentifier || "";

  const handleSelectionChange = useCallback(
    (newSelection) => {
      setSelection(newSelection);
      const fresh = createEmptyEntry(nextId);
      setEntries([fresh]);
      setExpanded(new Set([nextId]));
      setNextId((p) => p + 1);
      setSigners([]);
      setLatestSubmission(null);
      setContactsByCode({});
      setLatestByCode({});
      prefilledCodesRef.current.clear();
    },
    [nextId]
  );

  const addEntry = useCallback(() => {
    const ne = createEmptyEntry(nextId);
    setEntries((p) => [...p, ne]);
    setExpanded((p) => new Set([...p, nextId]));
    setNextId((p) => p + 1);
  }, [nextId]);

  const removeEntry = useCallback((id) => {
    setEntries((p) => p.filter((e) => e.id !== id));
    setExpanded((p) => {
      const n = new Set(p);
      n.delete(id);
      return n;
    });
  }, []);

  const updateEntry = useCallback(
    (id, partial) => setEntries((p) => p.map((e) => (e.id === id ? { ...e, ...partial } : e))),
    []
  );

  const toggle = useCallback((id) => {
    setExpanded((p) => {
      const n = new Set(p);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  }, []);

  const expandAll = useCallback(() => setExpanded(new Set(entries.map((e) => e.id))), [entries]);

  const collapseAll = useCallback(() => setExpanded(new Set()), []);

  const handleSignerEdit = useCallback((index, field, value) => {
    setSigners((prev) => prev.map((s, i) => (i === index ? { ...s, [field]: value } : s)));
  }, []);

  const handlePrepChange = useCallback((field, value) => setPrep((p) => ({ ...p, [field]: value })), []);
  const handleConfirmChange = useCallback(
    (field, value) => setConfirm((p) => ({ ...p, [field]: value })),
    []
  );

  const validationErrors = useMemo(
    () => validateSubmission({ selection, preparer: prep, entries, signers, confirm }),
    [selection, prep, entries, signers, confirm]
  );

  const filledCount = useMemo(() => entries.filter((e) => e.subagency).length, [entries]);

  // One-shot scroll to the first error after a Submit click. Reads the flag
  // set by handleSubmit, scrolls, then clears it — re-renders triggered by
  // unrelated edits / follow-up Submits don't re-scroll.
  useEffect(() => {
    if (!shouldScrollOnNextRenderRef.current) return;
    shouldScrollOnNextRenderRef.current = false;
    const target = document.querySelector("[data-error-path]");
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  });

  const handleSubmit = () => {
    if (submitting) return;
    setSubmitError(null);
    setSubmitAttempted(true);

    if (validationErrors.length > 0) {
      const first = validationErrors[0];
      setStep(first.step);
      // If the first error lives on a subagency card, make sure the card is
      // expanded so its DOM exists for the scroll target.
      const entryMatch = first.path.match(/^entries\[(.+?)\]\./);
      if (entryMatch) {
        const entryId = Number(entryMatch[1]);
        setExpanded((prev) => (prev.has(entryId) ? prev : new Set([...prev, entryId])));
      }
      shouldScrollOnNextRenderRef.current = true;
      return;
    }

    setConfirmOpen(true);
  };

  const buildPdfArgs = () => ({
    selection,
    parentOptions,
    subagenciesBySelection,
    entries,
    signers,
    preparer: prep,
    confirmRecipients: confirm,
    contactsByCode,
    userEmail,
    taxRollYear
  });

  const handleConfirmedSubmit = async () => {
    setSubmitting(true);
    try {
      const pdfBase64 = buildSubmissionPdfBase64(buildPdfArgs());
      const payload = buildSubmissionPayload({
        userEmail,
        selection,
        signers,
        preparer: prep,
        confirmRecipients: confirm,
        entries,
        pdfBase64
      });
      const response = await submitPreliminaryNotification(payload);
      setSubmitResult(response);
    } catch (err) {
      logError("Submit failed:", err);
      setSubmitError(apiErrorMessage(err));
    } finally {
      setSubmitting(false);
      setConfirmOpen(false);
    }
  };

  const handleResendEmail = async () => {
    if (!submitResult?.submissionIdentifier) return;
    setResendingEmail(true);
    setResendError(null);
    try {
      const response = await resendConfirmationEmail(submitResult.submissionIdentifier);
      setSubmitResult((prev) => ({ ...prev, emailStatus: response.emailStatus }));
    } catch (err) {
      logError("Resend email failed:", err);
      setResendError(apiErrorMessage(err));
    } finally {
      setResendingEmail(false);
    }
  };

  const steps = [
    { label: "Agency Info", onClick: () => setStep(0) },
    { label: "Direct Charges", onClick: () => setStep(1) },
    { label: "Authorized Signer", onClick: () => setStep(2) },
    { label: "Certification", onClick: () => setStep(3) }
  ];

  return (
    <div
      style={{
        minHeight: "100vh",
        background: colors.cream
      }}
    >
      {submissionPeriodError ? (
        <div style={{ padding: "3rem", textAlign: "center", maxWidth: 800, margin: "0 auto" }}>
          <div
            style={{
              color: colors.danger,
              fontSize: "1.5rem",
              fontWeight: 600,
              marginBottom: "1rem"
            }}
          >
            Submission Period Closed
          </div>
          <div style={{ color: colors.darkGray, fontSize: "1.125rem" }}>{submissionPeriodError}</div>
        </div>
      ) : (
        <>
          {!verified && token && (
            <PreFormPopup
              token={token}
              userEmail={userEmail}
              otpDisabled={otpDisabled}
              onVerified={() => setVerified(true)}
            />
          )}

          {/* Header */}
          <div
            style={{
              background: `linear-gradient(180deg, ${colors.navy} 0%, ${colors.navyLight} 100%)`,
              paddingBottom: verified ? 80 : 30
            }}
          >
            <div
              style={{
                padding: "clamp(16px, 4vw, 24px) clamp(16px, 4vw, 32px) clamp(12px, 2vw, 20px)",
                maxWidth: 960,
                margin: "0 auto"
              }}
            >
              {verified && userEmail && (
                <p
                  style={{
                    margin: 0,
                    fontSize: 14,
                    color: `${colors.white}bb`,
                    textAlign: "right",
                    wordBreak: "break-word"
                  }}
                >
                  Signed in as <strong style={{ color: `${colors.white}` }}>{userEmail}</strong>
                </p>
              )}
              <div style={{ height: 1, background: `${colors.white}22`, margin: "20px 0" }} />
              <div style={{ display: "flex", gap: 16, marginBottom: 6, alignItems: "flex-start" }}>
                <div
                  style={{
                    width: 44,
                    height: 44,
                    flexShrink: 0,
                    background: colors.gold,
                    margin: "6px 0",
                    borderRadius: 8,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}
                >
                  <DescriptionIcon sx={{ color: colors.white, fontSize: 24 }} />
                </div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <h1
                    style={{
                      margin: 0,
                      fontWeight: 700,
                      fontSize: "clamp(18px, 3.2vw, 28px)",
                      lineHeight: 1.2,
                      color: colors.white
                    }}
                  >
                    Preliminary Notification of Direct Charges
                  </h1>
                  <p
                    style={{
                      margin: "4px 0 0",
                      fontSize: "clamp(12px, 1.6vw, 14px)",
                      color: `${colors.white}`
                    }}
                  >
                    San Bernardino County — Auditor-Controller/Treasurer/Tax Collector — Property Tax Division
                  </p>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: 12,
                      marginTop: 16
                    }}
                  >
                    <span
                      style={{
                        background: `${colors.gold}33`,
                        color: colors.goldLight,
                        padding: "4px 14px",
                        borderRadius: 20,
                        fontSize: 13,
                        fontWeight: 600,
                        border: `1px solid ${colors.gold}4D`,
                        whiteSpace: "nowrap"
                      }}
                    >
                      Tax Roll Year: {taxRollYear}
                    </span>
                    <span style={{ fontSize: 13, color: `${colors.white}bb` }}>
                      Form must be submitted by the statutory deadline
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {verified && (
            <>
              {/* Form */}
              <div
                style={{
                  maxWidth: 960,
                  margin: "-64px auto 0",
                  padding: "0 32px 64px",
                  position: "relative"
                }}
              >
                <div
                  style={{
                    background: colors.white,
                    borderRadius: 12,
                    padding: 32,
                    border: `1px solid ${colors.borderLight}`,
                    boxShadow: `0 1px 3px ${colors.navy}0A`
                  }}
                >
                  <StepIndicator steps={steps} currentStep={step} />

                  <p
                    style={{
                      margin: "0 0 16px",
                      fontSize: 12,
                      color: colors.error,
                      fontStyle: "italic"
                    }}
                  >
                    Fields marked with an{" "}
                    <span style={{ color: colors.error, fontWeight: 700 }}>asterisk (*)</span> are required.
                  </p>

                  {(loadError ||
                    submitError ||
                    (submitAttempted && validationErrors.length > 0 && !submitResult) ||
                    submitResult) && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 16, marginBottom: 16 }}>
                      {loadError && (
                        <InfoNote variant="error">
                          <strong>Failed to load reference data:</strong> {loadError}
                        </InfoNote>
                      )}
                      {submitError && (
                        <InfoNote variant="error">
                          <strong>Submission failed.</strong> Please correct and try again. If the problem
                          persists, contact{" "}
                          <a
                            href="mailto:ptassessments@sbcountyatc.gov"
                            style={{
                              color: colors.error,
                              textDecoration: "underline",
                              textUnderlineOffset: "2px"
                            }}
                          >
                            ptassessments@sbcountyatc.gov
                          </a>
                          . <br /> <strong>Details:</strong> {submitError}
                        </InfoNote>
                      )}
                      {submitAttempted && validationErrors.length > 0 && !submitResult && (
                        <InfoNote variant="error">
                          <p style={{ margin: "0 0 6px" }}>
                            <strong>Please fix the following before submitting:</strong>
                          </p>
                          <ul style={{ margin: 0, paddingLeft: 20 }}>
                            {validationErrors.map((err) => (
                              <li key={err.path + err.message}>{err.message}</li>
                            ))}
                          </ul>
                        </InfoNote>
                      )}
                      {submitResult && (
                        <InfoNote variant="success">
                          Submission received. ID: <strong>{submitResult.submissionIdentifier}</strong> —
                          saved {submissionDate} with {submitResult.subagencyCount} subagenc
                          {submitResult.subagencyCount === 1 ? "y" : "ies"}.
                        </InfoNote>
                      )}
                      {submitResult?.emailStatus === "sent" && (
                        <InfoNote variant="success">
                          Confirmation email sent to{" "}
                          <strong>
                            {submitResult.emailRecipients.length} recipient
                            {submitResult.emailRecipients.length === 1 ? "" : "s"}
                          </strong>
                          :
                          <ul style={{ margin: "6px 0 0", paddingLeft: 20 }}>
                            {submitResult.emailRecipients.map((email) => (
                              <li key={email}>{email}</li>
                            ))}
                          </ul>
                        </InfoNote>
                      )}
                      {submitResult?.emailStatus === "no-recipients" && (
                        <InfoNote variant="warning">
                          No confirmation email was sent because no recipients were selected or found.
                        </InfoNote>
                      )}
                      {submitResult?.emailStatus === "failed" && (
                        <InfoNote variant="warning">
                          <p style={{ margin: "0 0 10px" }}>
                            Your submission was saved, but{" "}
                            <strong>the confirmation email failed to send</strong>. You can retry without
                            resubmitting the form.
                          </p>
                          {resendError && (
                            <p style={{ margin: "0 0 10px", fontSize: 13, color: colors.error }}>
                              Retry failed: {resendError}
                            </p>
                          )}
                          <Button
                            onClick={handleResendEmail}
                            disabled={resendingEmail}
                            variant="outlined"
                            size="small"
                          >
                            {resendingEmail ? "Resending…" : "Resend confirmation email"}
                          </Button>
                        </InfoNote>
                      )}
                    </div>
                  )}
                  <ValidationContext.Provider
                    value={submitAttempted ? validationErrors : NO_VALIDATION_ERRORS}
                  >
                    {step === 0 && (
                      <AgencyInfoStep
                        selection={selection}
                        options={parentOptions}
                        loaded={jurisdictionsLoaded}
                        onSelectionChange={handleSelectionChange}
                      />
                    )}
                    {step === 1 && (
                      <DirectChargesStep
                        selection={selection}
                        subagenciesBySelection={subagenciesBySelection}
                        subagencyData={subagencyData}
                        entries={entries}
                        expanded={expanded}
                        taxRollYear={taxRollYear}
                        onAdd={addEntry}
                        onRemove={removeEntry}
                        onUpdate={updateEntry}
                        onToggle={toggle}
                        onExpandAll={expandAll}
                        onCollapseAll={collapseAll}
                      />
                    )}
                    {step === 2 && (
                      <AuthorizedSignerStep
                        selection={selection}
                        options={parentOptions}
                        signers={signers}
                        onSignerEdit={handleSignerEdit}
                        latestSubmission={latestSubmission}
                      />
                    )}
                    {step === 3 && (
                      <CertificationStep
                        selection={selection}
                        subagenciesBySelection={subagenciesBySelection}
                        entries={entries}
                        prep={prep}
                        onPrepChange={handlePrepChange}
                        confirm={confirm}
                        onConfirmChange={handleConfirmChange}
                        submissionDate={submissionDate}
                        submissionId={submissionId}
                        submitted={!!submitResult}
                      />
                    )}
                  </ValidationContext.Provider>

                  <FormNavButtons
                    step={step}
                    onPrevious={() => setStep(Math.max(0, step - 1))}
                    onNext={() => setStep(Math.min(3, step + 1))}
                    onSubmit={handleSubmit}
                    onPreviewPdf={() => openSubmissionPdfInNewTab(buildPdfArgs())}
                    submitting={submitting}
                    submitted={!!submitResult}
                  />
                </div>
                <p style={{ textAlign: "center", fontSize: 11, color: colors.textLight, marginTop: 24 }}>
                  TaxSys Direct Charges Portal — San Bernardino County ATC
                </p>
              </div>

              {/* Submission Confirmation Popup */}
              <ConfirmSubmitPopup
                open={confirmOpen}
                submitting={submitting}
                filledCount={filledCount}
                taxRollYear={taxRollYear}
                onConfirm={handleConfirmedSubmit}
                onCancel={() => setConfirmOpen(false)}
              />
            </>
          )}
        </>
      )}
    </div>
  );
}
